import React, { useState, useContext } from "react";
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useNavigate } from "react-router-dom";
import {
    Box, TextField, Button, Typography, Alert, Divider,
    IconButton, InputAdornment
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import GoogleIcon from "@mui/icons-material/Google";
import firebase from "firebase/compat/app";
import { FirebaseAuth } from "../../../components/FirebaseAuth/firebase";
import { AuthContext } from "../../../components/FirebaseAuth";
import { userSignIn } from "../../../libs/user";
import AuthLayout from "../../../components/AuthLayout";
import Loader from "../../../components/Loader";

const PERSONAL_DOMAINS = [
    'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'aol.com',
    'icloud.com', 'mail.com', 'protonmail.com', 'zoho.com', 'yandex.com'
];

const isPersonalEmail = (email) => {
    const domain = email.split('@')[1]?.toLowerCase();
    return PERSONAL_DOMAINS.includes(domain);
};

const SignUp = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { authUser } = useContext(AuthContext);

    const [fullName, setFullName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    // Redirect if already signed in (but not if we're in the middle of signing up).
    // Rendered declaratively to avoid updating router state during render.
    if (!loading && authUser.checked && authUser.user) {
        return <Navigate to="/" replace />;
    }

    const handleEmailSignUp = async (e) => {
        e.preventDefault();
        setError("");

        if (!fullName.trim()) {
            setError(t('auth.fullNameRequired'));
            return;
        }
        if (password.length < 8) {
            setError(t('auth.passwordTooShort'));
            return;
        }
        if (password !== confirmPassword) {
            setError(t('auth.passwordsDontMatch'));
            return;
        }

        setLoading(true);
        try {
            const result = await FirebaseAuth.auth().createUserWithEmailAndPassword(email, password);
            await result.user.updateProfile({ displayName: fullName.trim() });
            result.user.sendEmailVerification();
            userSignIn((success) => {
                if (success) {
                    setLoading(false);
                    navigate("/", { replace: true });
                } else {
                    setError(t('auth.accountCreatedSyncFailed'));
                    setLoading(false);
                }
            });
        } catch (err) {
            setError(err.message);
            setLoading(false);
        }
    };

    const handleGoogleSignUp = async () => {
        setError("");
        setLoading(true);
        try {
            const provider = new firebase.auth.GoogleAuthProvider();
            await FirebaseAuth.auth().signInWithPopup(provider);
            userSignIn((success) => {
                if (success) {
                    setLoading(false);
                    navigate("/", { replace: true });
                } else {
                    setError(t('auth.googleSignUpSyncFailed'));
                    setLoading(false);
                }
            });
        } catch (err) {
            setError(err.message);
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <AuthLayout>
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Loader text={t('auth.creatingAccount')} />
                </Box>
            </AuthLayout>
        );
    }

    return (
        <AuthLayout>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, textAlign: 'center' }}>
                {t('auth.signUpTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
                {t('auth.signUpSubtitle')}
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Box component="form" id="sign-up-form" name="sign-up" onSubmit={handleEmailSignUp} noValidate>
                <TextField
                    label={t('auth.fullName')}
                    fullWidth
                    required
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    sx={{ mb: 2 }}
                    autoComplete="name"
                />
                <TextField
                    label={t('auth.workEmail')}
                    type="email"
                    fullWidth
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    helperText={
                        email && isPersonalEmail(email)
                            ? t('auth.personalEmailHint')
                            : ""
                    }
                    FormHelperTextProps={{
                        sx: { color: 'warning.main' }
                    }}
                    sx={{ mb: 2 }}
                    autoComplete="email"
                />
                <TextField
                    label={t('auth.password')}
                    type={showPassword ? "text" : "password"}
                    fullWidth
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    helperText={t('auth.atLeast8Chars')}
                    sx={{ mb: 2 }}
                    autoComplete="new-password"
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small">
                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />
                <TextField
                    label={t('auth.confirmPassword')}
                    type={showConfirmPassword ? "text" : "password"}
                    fullWidth
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    sx={{ mb: 3 }}
                    autoComplete="new-password"
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end" size="small">
                                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />
                <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    size="large"
                    sx={{ mb: 2, textTransform: 'none', fontWeight: 600 }}
                >
                    {t('auth.createAccount')}
                </Button>
            </Box>

            <Divider sx={{ my: 2 }}>{t('auth.or')}</Divider>

            <Button
                variant="outlined"
                fullWidth
                size="large"
                startIcon={<GoogleIcon />}
                onClick={handleGoogleSignUp}
                sx={{ mb: 3, textTransform: 'none', fontWeight: 600 }}
            >
                {t('auth.signUpWithGoogle')}
            </Button>

            <Typography variant="body2" sx={{ textAlign: 'center' }}>
                {t('auth.haveAccount')}{" "}
                <Link to="/signin" style={{ textDecoration: 'none' }}>{t('auth.signInLink')}</Link>
            </Typography>
        </AuthLayout>
    );
};

export default SignUp;
